import { z } from 'zod';
import {
  aggregateBlocksToProjectContent,
  flattenProjectContent,
  type ProjectContent,
} from '../content/blockScope';
import type { LayoutBlock } from '../content/studio';
import { LayoutBlockSchema } from '../content/studio';
import { PageMetaSchema } from '../content/pageMeta';
import { ProjectConfigurationSchema } from '../theme/themeSchemas';

/** Canonical multi-page scoped publish payload (pages + globalBlocks + pageBlocks). */
export const PublishSnapshotScopedSchema = z.object({
  version: z.string().min(1),
  pages: z.array(PageMetaSchema),
  globalBlocks: z.array(LayoutBlockSchema),
  pageBlocks: z.record(z.string().uuid(), z.array(LayoutBlockSchema)),
  configuration: ProjectConfigurationSchema,
  compiledAt: z.string().min(1),
});

export type PublishSnapshotScoped = z.infer<typeof PublishSnapshotScopedSchema>;

/** Legacy flat-block snapshot (read adapter only). */
export const PublishSnapshotLegacySchema = z.object({
  version: z.string().min(1),
  blocks: z.array(LayoutBlockSchema),
  configuration: ProjectConfigurationSchema,
  compiledAt: z.string().min(1),
});

/** Accepts scoped or legacy flat snapshots during rollout. */
export const PublishSnapshotSchema = z.union([
  PublishSnapshotScopedSchema,
  PublishSnapshotLegacySchema,
]);

export type PublishSnapshot = z.infer<typeof PublishSnapshotSchema>;

export function isScopedPublishSnapshot(
  snapshot: PublishSnapshot,
): snapshot is PublishSnapshotScoped {
  return 'pages' in snapshot && 'globalBlocks' in snapshot && 'pageBlocks' in snapshot;
}

export function normalizePublishSnapshotToProjectContent(
  snapshot: PublishSnapshot,
): ProjectContent & {
  configuration: PublishSnapshotScoped['configuration'];
  version: string;
  compiledAt: string;
} {
  if (isScopedPublishSnapshot(snapshot)) {
    return {
      pages: snapshot.pages,
      globalBlocks: snapshot.globalBlocks,
      pageBlocks: snapshot.pageBlocks,
      configuration: snapshot.configuration,
      version: snapshot.version,
      compiledAt: snapshot.compiledAt,
    };
  }

  const indexPageId = crypto.randomUUID();
  const indexPage = PageMetaSchema.parse({
    pageId: indexPageId,
    slug: 'index',
    localizedTitle: { de: 'Startseite' },
    seo: {
      title: { de: 'Startseite — Web-Studio' },
      description: {
        de: 'Willkommen auf unserer Website. Entdecken Sie Speisekarte, Reservierung und Kontakt.',
      },
      openGraphImage: '',
      openGraphImageMode: 'auto',
      sitemapIndexPriority: 1,
    },
    isSystemRoot: false,
    displayOrder: 0,
  });

  const aggregated = aggregateBlocksToProjectContent([indexPage], snapshot.blocks);

  return {
    ...aggregated,
    configuration: snapshot.configuration,
    version: snapshot.version,
    compiledAt: snapshot.compiledAt,
  };
}

/** Flat block list for legacy edge/publish readers during scoped rollout. */
export function flattenPublishSnapshotBlocks(snapshot: PublishSnapshot): LayoutBlock[] {
  if (isScopedPublishSnapshot(snapshot)) {
    return flattenProjectContent({
      pages: snapshot.pages,
      globalBlocks: snapshot.globalBlocks,
      pageBlocks: snapshot.pageBlocks,
    });
  }
  return snapshot.blocks;
}

export const ProductionArtifactManifestSchema = z.object({
  version: z.string().min(1),
  projectId: z.string().uuid(),
  defaultLocale: z.string().min(2),
  supportedLocales: z.array(z.string().min(2)),
  /** Homepage path per locale (legacy readers). */
  localePages: z.record(z.string(), z.string().min(1)),
  /** Full locale × slug → relative artifact path (multi-page scoped). */
  pageManifest: z.record(z.string(), z.record(z.string(), z.string().min(1))).optional(),
  baseUri: z.string().url(),
  bakedAt: z.string().min(1),
});

export type ProductionArtifactManifest = z.infer<typeof ProductionArtifactManifestSchema>;

/** Relative path of the full publish snapshot inside a baked artifact folder. */
export const PUBLISH_SNAPSHOT_ARTIFACT_PATH = 'snapshot.json' as const;

export const PublishDiffEntrySchema = z.object({
  kind: z.enum(['configuration', 'block_added', 'block_removed', 'block_content']),
  label: z.string(),
  detail: z.string().optional(),
});

export type PublishDiffEntry = z.infer<typeof PublishDiffEntrySchema>;

export const PublishDiffLedgerSchema = z.object({
  hasProduction: z.boolean(),
  entries: z.array(PublishDiffEntrySchema),
  blockCount: z.number().int(),
  stagingReady: z.boolean(),
});

export type PublishDiffLedger = z.infer<typeof PublishDiffLedgerSchema>;
